import {yupResolver} from '@hookform/resolvers/yup'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {ProjectRepo} from 'app/hooks/api_repos'
import {useAppNavigate} from 'app/hooks/navigation/useAppNavigate'
import {useAuthProvider} from 'app/modules/auth'
import {queryClient} from 'app/react-query'
import React from 'react'
import {useForm} from 'react-hook-form'
import {useStylesCommon} from 'styles/useStyles'
import toastr from 'toastr'
import {emGetActiveTenantData} from 'utilities/emUserFeatures'
import {emSleep} from 'utilities/extensionMethods'
import {ProjectTypesDS} from 'utilities/projectTypes'
import * as Yup from 'yup'

interface Props {
  open: boolean
  setOpen: (b: boolean) => void
  creating: boolean
  setCreating: (b: boolean) => void
}
interface iCreateNewProject {
  projectName?: string
  projectDescription?: string
  project_type?: string
}

const initialValues: iCreateNewProject = {
  projectName: '',
  projectDescription: '',
  project_type: '',
}

const createProjectSchema = Yup.object({
  projectName: Yup.string().required('Project name is required'),
  projectDescription: Yup.string(),
  project_type: Yup.string().required('Project type is required'),
})

export default function CreateProjectDialog(props: Props): React.ReactElement {
  const {reduxUser} = useAuthProvider()
  const navigate = useAppNavigate()
  const classes = useStylesCommon()
  const {
    register,
    handleSubmit,
    formState: {errors, isSubmitting, defaultValues},
  } = useForm({
    resolver: yupResolver(createProjectSchema),
    mode: 'onChange',
    defaultValues: {
      projectName: '',
      projectDescription: '',
      project_type: '',
    },
  })

  console.log('CreateProjectDialog loading')
  const createProject = ProjectRepo.useCreate()

  const [project_type, setProjectType] = React.useState<string>(
    ProjectTypesDS?.find((s) => s.id === defaultValues?.project_type)?.id ?? ''
  )
  const currentTenantId = emGetActiveTenantData(reduxUser ?? {})?.id ?? ''

  const submitStep = async (values: typeof initialValues) => {
    props.setCreating(true)
    let id: string | undefined = ''
    try {
      const projectTypes = ProjectTypesDS?.filter((x) => x.id === values.project_type)
      const projectType = projectTypes && projectTypes.length ? projectTypes[0] : null
      if (!projectType) {
        throw Error(`Invalid project type id: ${values.project_type}`)
      }
      if (!currentTenantId) {
        throw Error(`Cannot create a project outside of a tenant.`)
      }
      if (!values.projectName) {
        throw Error(`A project name is required.`)
      }
      if (projectType.id) {
        const createdProject = await createProject.mutateAsync({
          data: {
            name: values.projectName,
            description: values.projectDescription ?? '',
            project_type: projectType.id,
            tenant_id: currentTenantId,
          },
        })
        id = createdProject?.id
      }
      //key for invalidation is route plus key, so the projectList is being pulled with onLoadGet now instead of projct repo
      const invKey = `std/project` + 'projectList'
      queryClient.invalidateQueries(invKey)
      props.setOpen(false)
    } catch (error: any) {
      console.warn(error)
      const errorMessage =
        error?.response?.data?.error || 'Unable to create project. Please try again.'
      toastr.error(errorMessage)
    } finally {
      //awaiting invalidation before saying done creating
      await emSleep(1500)
      props.setCreating(false)
    }
  }

  const onSubmit = handleSubmit((data) => {
    submitStep(data)
  })

  return (
    <Dialog
      fullWidth
      open={props.open}
      onClose={() => props.setOpen(false)}
      aria-labelledby='scroll-dialog-title'
      aria-describedby='scroll-dialog-description'
    >
      <DialogTitle id='scroll-dialog-title' mb={1}>
        Create A Project
      </DialogTitle>
      <DialogContent>
        <form autoComplete='off' onSubmit={onSubmit}>
          <Box display={'flex'} flexDirection={'column'} gap={2} mb={2}>
            <TextField
              fullWidth
              label={'Project Name'}
              variant='filled'
              placeholder={'Enter the name of your project...'}
              helperText={
                <span className={classes.validationTextError}>
                  {errors.projectName?.message ?? ''}
                </span>
              }
              {...register('projectName')}
            />

            <TextField
              fullWidth
              variant='filled'
              label={'Project Description'}
              placeholder={`Enter a description of your project...`}
              helperText={
                <span className={classes.validationTextError}>
                  {errors.projectDescription?.message ?? ''}
                </span>
              }
              {...register('projectDescription')}
            />

            <FormControl fullWidth>
              <InputLabel id='project-type-label'>Project Type</InputLabel>
              <Select
                variant='filled'
                fullWidth
                labelId='project-type-label'
                label={'Select Project Type'}
                value={project_type ?? ''}
                {...register('project_type', {
                  onChange: (event) =>
                    setProjectType(
                      ProjectTypesDS?.find((f) => f.id === event.target.value)?.id ?? ''
                    ),
                })}
              >
                <MenuItem value=''>Select a project type</MenuItem>
                {ProjectTypesDS.map((projectType) => (
                  <MenuItem key={projectType.id} value={projectType.id}>
                    {`${projectType.display_name}`}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                <span className={classes.validationTextError}>
                  {errors.project_type?.message ?? ''}
                </span>
              </FormHelperText>
            </FormControl>
          </Box>

          <Button variant='contained' type='submit'>
            {isSubmitting ? (
              <>
                Please wait... <CircularProgress />
              </>
            ) : (
              <Box display={'flex'}>
                Create Project
                <ArrowForwardIcon />
              </Box>
            )}
          </Button>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.setOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
